import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, CheckCircle2, Circle, Calendar } from 'lucide-react';

const CircleTasksTab = ({ tasks, members, user, newTask, setNewTask, handleCreateTask, handleToggleTask, handleClaimTask, addTaskDialogOpen, setAddTaskDialogOpen }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <h3 className="text-xl font-bold text-foreground">Circle Tasks</h3>
      <Dialog open={addTaskDialogOpen} onOpenChange={setAddTaskDialogOpen}>
        <DialogTrigger asChild>
          <Button className="btn-primary" data-testid="add-task-btn"><Plus className="w-4 h-4 mr-2" /> Ask for Help</Button>
        </DialogTrigger>
        <DialogContent className="surface-card">
          <DialogHeader>
            <DialogTitle>Ask for Help</DialogTitle>
            <DialogDescription>Let your circle know what you need - someone will step up.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="task-title">What do you need help with?</Label>
              <Input id="task-title" data-testid="task-title-input" placeholder="e.g., Pick up medication from CVS" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-description">Details (optional)</Label>
              <Textarea id="task-description" data-testid="task-description-input" placeholder="Any additional info that might help..." value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} className="resize-none" />
            </div>
            <div className="space-y-2">
              <Label>Ask someone specific (optional)</Label>
              <Select value={newTask.assigned_to} onValueChange={(value) => setNewTask({ ...newTask, assigned_to: value })}>
                <SelectTrigger data-testid="task-assign-select"><SelectValue placeholder="Leave open for anyone" /></SelectTrigger>
                <SelectContent>
                  {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-due-date">Due Date</Label>
              <Input id="task-due-date" type="date" data-testid="task-due-date-input" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} />
            </div>
            <Button type="submit" className="w-full btn-primary" data-testid="submit-task-btn">Post Help Request</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>

    <div className="space-y-4">
      {tasks.length > 0 ? tasks.map((task) => {
        const statusStyles = { completed: 'bg-emerald-100 text-emerald-700', in_progress: 'bg-cyan-100 text-cyan-700', pending: 'bg-amber-100 text-amber-700' };
        const statusLabels = { completed: 'Done', in_progress: 'In Progress', pending: 'To Do' };
        const priorityStyles = { high: 'bg-rose-100 text-rose-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-sky-100 text-sky-700' };
        return (
          <Card key={task.id} className="surface-card" data-testid={`task-${task.id}`}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <button onClick={() => handleToggleTask(task.id, task.status)} className="mt-1" data-testid={`toggle-task-${task.id}`}>
                  {task.status === 'completed' ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <Circle className="w-6 h-6 text-muted-foreground" />}
                </button>
                <div className="flex-1">
                  <h4 className={`font-semibold text-foreground ${task.status === 'completed' ? 'line-through opacity-60' : ''}`}>{task.title}</h4>
                  {task.description && <p className="text-sm text-muted-foreground mt-1">{task.description}</p>}
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                    {task.due_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Due: {new Date(task.due_date).toLocaleDateString()}</span>}
                    <span className={`px-2 py-1 rounded-full ${statusStyles[task.status] || statusStyles.pending}`}>{statusLabels[task.status] || 'To Do'}</span>
                    {task.priority && <span className={`px-2 py-1 rounded-full ${priorityStyles[task.priority] || priorityStyles.medium}`}>{task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}</span>}
                    {!task.assigned_to && task.status !== 'completed' && <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700">Needs a volunteer</span>}
                    {task.assigned_to && <span className="text-cyan-600">{task.assigned_to === user.id ? '✓ You claimed this' : `✓ Claimed by ${members.find(m => m.user_id === task.assigned_to)?.user?.name || 'someone'}`}</span>}
                  </div>
                  {!task.assigned_to && task.status !== 'completed' && (
                    <Button size="sm" className="mt-3 btn-secondary" onClick={() => handleClaimTask(task.id)} data-testid={`claim-task-${task.id}`}>I can help with this</Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      }) : (
        <div className="text-center py-12">
          <CheckCircle2 className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground">No help requests yet. Ask for help to get started!</p>
        </div>
      )}
    </div>
  </div>
);

export default CircleTasksTab;
